import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, Image as ImageIcon, Loader2, Lock, Paperclip, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { API_BASE, ApiError, del, get } from '../../lib/api';
import { cn } from '../../lib/cn';
import { relativeTime } from '../../lib/format';
import { useSession } from '../../lib/session';
import { useConfirm } from '../../components/dialog';
import { useToast } from '../../components/toast';
import { Badge, Button, Card, Skeleton } from '../../components/ui';

/**
 * Attachments on a ticket.
 *
 * Uploading is two-phase, because it has to be: a file is attached while the
 * message that will carry it is still being written. `useAttachmentUpload`
 * holds the ids of files uploaded but not yet sent, and the message is posted
 * with those ids so the server attaches them in the same transaction. Anything
 * never sent is swept server-side, so abandoning a draft leaves nothing behind.
 */

export interface PendingUpload {
  id: number;
  filename: string;
  contentType: string;
  sizeBytes: number;
}

export interface StoredAttachment {
  id: number;
  filename: string;
  content_type: string;
  size_bytes: string;
  is_inline: boolean;
  content_id: string | null;
  created_at: string;
  source: string;
  entity_type: string;
  entity_id: number | null;
  /** The kind of message the file came with, when it came with one. */
  conversation_kind: string | null;
  uploaded_by_name: string | null;
}

/** The server refuses more than this in one message; saying so first is kinder. */
export const MAX_FILES_PER_MESSAGE = 20;

/** Bytes, rendered the way a person reads them. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

function iconFor(contentType: string) {
  return contentType.startsWith('image/') ? ImageIcon : FileText;
}

/** Images the browser can show and the server serves inline. */
const PREVIEWABLE = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);

export const attachmentUrl = (id: number, download = false) =>
  `${API_BASE}/attachments/${id}${download ? '?download=true' : ''}`;

/** Everything attached to a ticket — one query, read by the rail and the timeline. */
export function useTicketAttachments(number: string) {
  return useQuery<{ data: StoredAttachment[] }, ApiError>({
    queryKey: ['ticket-attachments', number],
    queryFn: () => get(`/tickets/${number}/attachments`),
  });
}

/**
 * Sends one file.
 *
 * The response is read as text and parsed defensively: a proxy restarting
 * during a deploy answers with an empty body, and `response.json()` on that
 * put "Unexpected end of JSON input" in front of the person. A failure to reach
 * the server at all says so in words rather than "Failed to fetch".
 */
async function uploadFile(file: File): Promise<PendingUpload> {
  const form = new FormData();
  form.append('file', file);

  let response: Response;
  try {
    // FormData, so no JSON content type — the browser sets the multipart
    // boundary itself and overriding it breaks the parse.
    response = await fetch(`${API_BASE}/attachments`, {
      method: 'POST',
      body: form,
      credentials: 'same-origin',
    });
  } catch {
    throw new ApiError(0, 'network_error', 'Could not reach the server. Check your connection and try again.');
  }

  const text = await response.text();
  let body: {
    data?: PendingUpload;
    error?: { code?: string; message?: string; details?: Array<{ path: string; message: string }> };
  } = {};
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    body = {};
  }

  if (!response.ok || !body.data) {
    throw new ApiError(
      response.status,
      (body.error?.code as never) ?? 'internal_error',
      body.error?.details?.[0]?.message ?? body.error?.message ?? `The upload failed (${response.status}).`,
      body.error?.details,
    );
  }
  return body.data;
}

/**
 * The upload half of a composer.
 *
 * Files upload immediately on selection rather than on submit, so a slow
 * network is spent while the person is still typing. Every file in flight is
 * counted, so a caller can refuse to send while one is still on its way — a
 * single shared mutation used to report only the last file, and a message sent
 * a moment too early simply went without the others.
 */
export function useAttachmentUpload() {
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [inFlight, setInFlight] = useState<Array<{ key: number; filename: string }>>([]);
  const [error, setError] = useState<string | null>(null);
  // Bumped by `clear`, so an upload that finishes after its message was sent
  // (or its draft discarded) is dropped rather than appearing in an empty box.
  const generation = useRef(0);
  const nextKey = useRef(1);

  const send = async (file: File, gen: number, key: number) => {
    try {
      const stored = await uploadFile(file);
      if (generation.current !== gen) {
        void del(`/attachments/${stored.id}`).catch(() => {});
        return;
      }
      setPending((previous) => [...previous, stored]);
    } catch (failure) {
      if (generation.current === gen) {
        const reason = failure instanceof ApiError ? failure.message : 'The upload failed.';
        setError(`“${file.name}” could not be attached. ${reason}`);
      }
    } finally {
      setInFlight((previous) => previous.filter((item) => item.key !== key));
    }
  };

  return {
    pending,
    error,
    /** Files still on their way. */
    uploading: inFlight,
    busy: inFlight.length > 0,
    add: (files: FileList | File[]) => {
      let list = Array.from(files);
      const room = MAX_FILES_PER_MESSAGE - pending.length - inFlight.length;
      if (list.length > room) {
        setError(
          room <= 0
            ? `A message can carry ${MAX_FILES_PER_MESSAGE} files at most. Remove one to add another.`
            : `A message can carry ${MAX_FILES_PER_MESSAGE} files at most, so only the first ${room} were added.`,
        );
        list = list.slice(0, Math.max(room, 0));
      } else {
        setError(null);
      }
      for (const file of list) {
        const key = nextKey.current++;
        setInFlight((previous) => [...previous, { key, filename: file.name }]);
        void send(file, generation.current, key);
      }
    },
    remove: (id: number) => {
      setPending((previous) => previous.filter((file) => file.id !== id));
      // Best effort. An unsent file is swept server-side anyway, so a failed
      // delete here costs nothing but a little storage until then.
      void del(`/attachments/${id}`).catch(() => {});
    },
    clear: () => {
      generation.current += 1;
      setPending([]);
      setInFlight([]);
      setError(null);
    },
    ids: () => pending.map((file) => file.id),
  };
}

export type AttachmentUpload = ReturnType<typeof useAttachmentUpload>;

/** The file picker and the list of what is queued to send. */
export function AttachmentPicker({
  upload,
  disabled,
  label = 'Attach',
}: {
  upload: AttachmentUpload;
  disabled?: boolean;
  label?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  return (
    <div
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        if (!disabled && event.dataTransfer.files.length > 0) upload.add(event.dataTransfer.files);
      }}
      className={cn('rounded-control transition-colors', dragging && 'bg-accent-soft ring-1 ring-accent')}
    >
      <input
        ref={input}
        type="file"
        multiple
        hidden
        data-testid="attachment-input"
        onChange={(event) => {
          if (event.target.files?.length) upload.add(event.target.files);
          // Reset, so selecting the same file twice in a row still fires.
          event.target.value = '';
        }}
      />

      <div className="flex flex-wrap items-center gap-1.5">
        <Button size="sm" variant="ghost" disabled={disabled} onClick={() => input.current?.click()}>
          <Paperclip aria-hidden className="size-3.5" />
          {label}
        </Button>

        {upload.pending.map((file) => {
          const Icon = iconFor(file.contentType);
          return (
            <span
              key={file.id}
              className="inline-flex max-w-full items-center gap-1 rounded-control border border-line bg-sunken px-1.5 py-0.5 text-mini text-ink"
            >
              <Icon aria-hidden className="size-3 shrink-0 text-ink-subtle" />
              <bdi className="max-w-40 truncate">{file.filename}</bdi>
              <span className="text-ink-subtle">{formatBytes(file.sizeBytes)}</span>
              <button
                type="button"
                onClick={() => upload.remove(file.id)}
                aria-label={`Remove ${file.filename}`}
                className="rounded p-0.5 text-ink-subtle hover:text-critical-ink"
              >
                <X aria-hidden className="size-3" />
              </button>
            </span>
          );
        })}

        {upload.uploading.map((file) => (
          <span
            key={file.key}
            className="inline-flex items-center gap-1 rounded-control border border-dashed border-line-strong px-1.5 py-0.5 text-mini text-ink-muted"
          >
            <Loader2 aria-hidden className="size-3 animate-spin" />
            <bdi className="max-w-40 truncate">{file.filename}</bdi>
            <span className="sr-only-focusable">uploading</span>
          </span>
        ))}
      </div>

      {upload.error && (
        <p role="alert" className="mt-1 text-mini text-critical-ink">
          {upload.error}
        </p>
      )}
    </div>
  );
}

/**
 * The files that came with one message, under it.
 *
 * Images are shown, not listed. A requester's screenshot pasted into an Outlook
 * reply arrives as an inline image, and those were filtered out of every screen
 * — the agent saw "see the error below" and nothing below it.
 */
export function MessageAttachments({ files }: { files: StoredAttachment[] }) {
  if (files.length === 0) return null;
  const images = files.filter((file) => PREVIEWABLE.has(file.content_type));
  const others = files.filter((file) => !PREVIEWABLE.has(file.content_type));

  return (
    <div className="mt-3 space-y-2">
      {images.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label="Images">
          {images.map((file) => (
            <li key={file.id}>
              <a
                href={attachmentUrl(file.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="block overflow-hidden rounded-control border border-line bg-sunken transition-colors hover:border-accent-line"
                title={file.filename}
              >
                <img
                  src={attachmentUrl(file.id)}
                  alt={file.filename}
                  loading="lazy"
                  className="max-h-48 max-w-full object-contain sm:max-w-xs"
                />
              </a>
            </li>
          ))}
        </ul>
      )}
      {others.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" aria-label="Files">
          {others.map((file) => {
            const Icon = iconFor(file.content_type);
            return (
              <li
                key={file.id}
                className="inline-flex max-w-full items-center gap-1.5 rounded-control border border-line bg-surface px-2 py-1 text-mini"
              >
                <Icon aria-hidden className="size-3.5 shrink-0 text-ink-subtle" />
                <a
                  href={attachmentUrl(file.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="min-w-0 truncate text-ink hover:text-accent-ink hover:underline"
                >
                  <bdi>{file.filename}</bdi>
                </a>
                <span className="shrink-0 tabular-nums text-ink-subtle">{formatBytes(Number(file.size_bytes))}</span>
                <a
                  href={attachmentUrl(file.id, true)}
                  download
                  aria-label={`Download ${file.filename}`}
                  className="shrink-0 rounded p-0.5 text-ink-subtle hover:text-ink"
                >
                  <Download aria-hidden className="size-3.5" />
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Everything already attached to a ticket, in the rail. */
export function AttachmentsPanel({ number }: { number: string }) {
  const queryClient = useQueryClient();
  const { can, session } = useSession();
  const confirm = useConfirm();
  const toast = useToast();
  const attachments = useTicketAttachments(number);
  const mayRemove = Boolean(session?.is_agent) && can('ticket.edit');

  const remove = useMutation<unknown, ApiError, StoredAttachment>({
    mutationFn: (file) => del(`/attachments/${file.id}`),
    onSuccess: (_result, file) => {
      toast.success(`Removed “${file.filename}”`);
      void queryClient.invalidateQueries({ queryKey: ['ticket-attachments', number] });
    },
    onError: (error) => toast.error(error),
  });

  if (attachments.isLoading) {
    return (
      <Card className="space-y-2 p-3" aria-busy>
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-full" />
      </Card>
    );
  }

  // A failure says so. An empty list on error claimed the ticket had no files.
  if (attachments.isError) {
    return (
      <Card className="p-3">
        <p role="alert" className="text-mini text-critical-ink">
          The attachments could not be loaded.
        </p>
        <Button size="xs" className="mt-2" onClick={() => void attachments.refetch()}>
          Try again
        </Button>
      </Card>
    );
  }

  const files = Array.isArray(attachments.data?.data) ? attachments.data.data : [];
  if (files.length === 0) return null;
  const latest = files[files.length - 1]!;

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 border-b border-line bg-sunken/40 px-3 py-2">
        <Paperclip aria-hidden className="size-3.5 text-ink-subtle" />
        <h2 className="text-body font-medium text-ink">Attachments</h2>
        <span className="rounded-full bg-raised px-1.5 py-px text-mini tabular-nums text-ink-muted">
          {files.length}
        </span>
      </div>

      <ul className="divide-y divide-line">
        {files.map((file) => {
          const Icon = iconFor(file.content_type);
          return (
            <li key={file.id} className="flex items-center gap-2 px-3 py-1.5">
              <Icon aria-hidden className="size-3.5 shrink-0 text-ink-subtle" />
              <a
                href={attachmentUrl(file.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="min-w-0 flex-1 truncate text-body text-ink hover:text-accent-ink hover:underline"
              >
                <bdi>{file.filename}</bdi>
              </a>
              {file.conversation_kind === 'note' && (
                <Badge tone="caution" title="Attached to an internal note">
                  <Lock aria-hidden className="size-2.5" />
                  Internal
                </Badge>
              )}
              {file.is_inline && <Badge tone="neutral">In email</Badge>}
              <span className="shrink-0 text-mini tabular-nums text-ink-subtle">
                {formatBytes(Number(file.size_bytes))}
              </span>
              <a
                href={attachmentUrl(file.id, true)}
                download
                aria-label={`Download ${file.filename}`}
                className="shrink-0 rounded p-0.5 text-ink-subtle hover:text-ink"
              >
                <Download aria-hidden className="size-3.5" />
              </a>
              {mayRemove && (
                <button
                  type="button"
                  disabled={remove.isPending && remove.variables?.id === file.id}
                  onClick={async () => {
                    const ok = await confirm({
                      title: `Remove “${file.filename}”?`,
                      description: 'It disappears from this ticket for everybody. The message it came with stays.',
                      confirmLabel: 'Remove file',
                      tone: 'danger',
                    });
                    if (ok) remove.mutate(file);
                  }}
                  aria-label={`Remove ${file.filename}`}
                  className="shrink-0 rounded p-0.5 text-ink-subtle hover:text-critical-ink disabled:opacity-50"
                >
                  <X aria-hidden className="size-3.5" />
                </button>
              )}
            </li>
          );
        })}
      </ul>

      <p className="border-t border-line px-3 py-1.5 text-mini text-ink-subtle">
        Latest added {relativeTime(latest.created_at)}
        {latest.uploaded_by_name ? (
          <>
            {' '}
            by <bdi>{latest.uploaded_by_name}</bdi>
          </>
        ) : null}
      </p>
    </Card>
  );
}
